import { z } from 'zod/v4';
import { type CoupleActivity, isExistingCouple } from '../couple-profile/existing-couple';
import type { PlannerUIMessage } from '../types';
import type { WeddingContextSummary } from '../wedding-context-summary';

// The text of the hidden user turn that makes the coach speak first. Behaviour depends on
// the message's `coachIntent` flag, not on this text.
export const COACH_OPENER_MESSAGE = 'Please begin our first wedding-planning conversation.';

// Whether the couple has a plan: a date or a checklist on file.
export const hasPlanOnFile = (profile: WeddingContextSummary['profile'] | null | undefined) =>
  Boolean(profile?.date || profile?.checklistInitialised);

/**
 * Whether the opener is a check-in rather than a first meeting: a plan or planning is on
 * record. The opener chip's label and the opener both use this rule, so they agree.
 */
export const opensWithCheckIn = (ctx: CoupleActivity | undefined): boolean =>
  hasPlanOnFile(ctx?.profile) || isExistingCouple(ctx);

/**
 * Opener texts from stored threads that have no `coachIntent` flag. Without this list, the
 * couple would see words they never typed after a reload. Remove once no such thread is left.
 */
const LEGACY_OPENER_TEXTS: readonly string[] = [
  COACH_OPENER_MESSAGE,
  "I'd love to meet my wedding coach and talk through what we're imagining.",
];

export const CoachIntentSchema = z.object({
  trigger: z.literal('opener'),
});

export type CoachIntent = z.infer<typeof CoachIntentSchema>;
/**
 * True for the hidden user turn the couple never typed. Rendering and delivery status
 * skip it. Found by its flag; the text check covers stored threads without the flag.
 */
export const isSyntheticOpener = (message: PlannerUIMessage): boolean => {
  if (message.role !== 'user') {
    return false;
  }
  if (message.metadata?.coachIntent?.trigger === 'opener') {
    return true;
  }
  const text = (message.parts ?? [])
    .map((part) => (part.type === 'text' ? part.text : ''))
    .join('')
    .trim();
  return LEGACY_OPENER_TEXTS.includes(text);
};
